import { useEffect } from 'react'

type KeyboardShortcuts = Record<string, () => void>

/**
 * Hook for registering keyboard shortcuts globally
 * 
 * @param shortcuts - Object mapping keys to callback functions
 * @param enabled - Whether shortcuts are enabled (default: true)
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcuts, enabled = true) => {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcuts when user is typing in form fields
      const target = event.target as HTMLElement
      const isTyping = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.contentEditable === 'true' ||
                      target.getAttribute('role') === 'textbox'
      
      if (isTyping) return

      // Ignore shortcuts when modifier keys are pressed (except Shift for capital letters)
      if (event.ctrlKey || event.metaKey || event.altKey) return

      // Get the pressed key in lowercase
      const key = event.key.toLowerCase()
      
      // Check if we have a handler for this key
      if (shortcuts[key]) {
        event.preventDefault()
        shortcuts[key]()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shortcuts, enabled])
}

type ModifierKeyboardShortcuts = Record<string, () => void>

/**
 * Hook for registering keyboard shortcuts with modifier keys (Cmd/Ctrl)
 * Useful for shortcuts that should work even when typing in form fields
 * 
 * @param shortcuts - Object mapping key combinations to callback functions (e.g., 'Enter' for Cmd/Ctrl+Enter)
 * @param enabled - Whether shortcuts are enabled (default: true)
 */
export const useModifierKeyboardShortcuts = (shortcuts: ModifierKeyboardShortcuts, enabled = true) => {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts with Cmd (Mac) or Ctrl (Windows/Linux)
      if (!event.metaKey && !event.ctrlKey) return

      // Get the pressed key
      const key = event.key
      
      // Check if we have a handler for this key combination
      if (shortcuts[key]) {
        event.preventDefault()
        shortcuts[key]()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shortcuts, enabled])
}